import { z } from 'zod'

export const RiskDimensionConfigSchema = z.object({
  name: z.string(),
  label: z.string(),
  description: z.string(),
  weight: z.number(),
})

export type RiskDimensionConfig = z.infer<typeof RiskDimensionConfigSchema>

export const DEFAULT_INHERENT_RISK_DIMENSION_NAMES = [
  'data_sensitivity',
  'system_access_level',
  'regulatory_scope',
  'business_criticality',
  'data_volume',
  'engagement_type',
  'geographic_risk',
] as const

export type DefaultInherentRiskDimensionName = (typeof DEFAULT_INHERENT_RISK_DIMENSION_NAMES)[number]

export const DEFAULT_INHERENT_RISK_DIMENSIONS: (RiskDimensionConfig & { name: DefaultInherentRiskDimensionName })[] = [
  {
    name: 'data_sensitivity',
    label: 'Data Sensitivity',
    description: `What types of data will the vendor access, store, or process on behalf of the organisation? Consider whether the data includes personally identifiable information (PII), financial records, health or medical data, confidential intellectual property, employee data, or sensitive commercial information. Assess whether the vendor will have access to production data or only anonymised/test data.

**Factors**

Most sensitive data type — single-select of the worst type in scope.
  - None / public — 1
  - Business contact data — 2
  - Personal data (PII) — 3
  - Special-category data (health, biometric, precise location) — 4
  - Payment card data — 5

Data stage.
  - Test / synthetic only — 1
  - Mixed — 3
  - Production / live — 5

Data used to train the vendor's models.
  - No, contractually excluded — 1
  - Unclear — 3
  - Yes — 5`,
    weight: 0.25,
  },
  {
    name: 'system_access_level',
    label: 'System Access Level',
    description: `What level of access will the vendor require to internal systems, networks, or infrastructure? Consider whether access is read-only or includes write/modify/delete capabilities, whether they will access production environments or only lower environments, whether admin or privileged access is required, and whether they will connect to internal networks via VPN or direct integration.

**Factors**

Access level — single-select.
  - No system access — 1
  - Scoped API / data exchange — 2
  - Application user access — 3
  - Privileged / admin — 4
  - Internal network or production infrastructure — 5

Access persistence — capped below critical, as persistence alone is a modifier.
  - One-off / time-boxed — 2
  - Ongoing standing access — 4`,
    weight: 0.2,
  },
  {
    name: 'regulatory_scope',
    label: 'Regulatory Scope',
    description: `Is this engagement subject to regulatory, legal, or compliance requirements that impose obligations on how data is handled or how the vendor must operate? Consider whether the engagement involves data subjects in regulated jurisdictions, or whether it touches systems or processes that are in scope for internal audit or external certification.

**Factors**

Regimes in scope — multi-select, driven by the worst regime or by the overlap between them.
  - None — 1
  - GDPR / UK GDPR — 3
  - Sector-specific (DORA, HIPAA, financial regulation) — 4
  - Multiple high-stakes regimes overlapping — 5

EU AI Act in scope.
  - No — 1
  - Limited-risk system — 3
  - High-risk system — 5`,
    weight: 0.15,
  },
  {
    name: 'business_criticality',
    label: 'Business Criticality',
    description: `How critical is this vendor to the continuity of business operations? Consider whether the vendor provides a service that, if unavailable, would halt or severely degrade core business processes, revenue generation, or customer-facing services. Assess the availability of alternative vendors or internal fallback options, and how quickly the organisation could recover from a vendor failure.

**Factors**

Outage impact — the primary criticality signal.
  - No material impact — 1
  - Degrades operations — 2
  - Disrupts a core process — 4
  - Halts revenue / critical operations — 5

Substitutability.
  - Easily replaced — 1
  - Some switching cost — 3
  - Deeply embedded / no alternative — 5

Downtime tolerance.
  - Days — 1
  - Hours — 3
  - Near-zero / real-time — 5

Supports a regulated or otherwise important business function.
  - No — 1
  - Yes — 5

Spend band — capped at 4, as spend signals dependence rather than risk on its own.
  - Under £50k — 1
  - £50k-250k — 2
  - £250k-1m — 3
  - Over £1m — 4`,
    weight: 0.15,
  },
  {
    name: 'data_volume',
    label: 'Data Volume',
    description: `How much data will the vendor handle, transfer, or process over the course of this engagement? Consider whether the engagement involves bulk exports, data warehousing, or analytics over large datasets, as these increase exposure compared to narrow, record-level access.

**Factors**

Records in scope.
  - Minimal / none — 1
  - Thousands — 2
  - Hundreds of thousands — 3
  - Millions — 4
  - Bulk / whole customer base — 5

Processing frequency.
  - One-off — 1
  - Periodic — 3
  - Continuous / real-time — 5`,
    weight: 0.1,
  },
  {
    name: 'engagement_type',
    label: 'Engagement Type',
    description: `What is the nature and structure of this vendor engagement? Consider whether it is a software-as-a-service (SaaS) product, a professional services arrangement, outsourced operations, a one-off project, or an embedded staffing model. Also consider whether the vendor will sub-process data through third parties, and whether the engagement involves human access to data vs. automated processing only.

**Factors**

Engagement nature.
  - One-off / time-boxed project — 2
  - Ongoing service — 3
  - Continuous processing — 5

AI in the product.
  - No — 2
  - Yes, assistive — 3
  - Yes, autonomous or decisioning on the organisation's data — 5

Onward sub-processing — capped at 4.
  - None — 2
  - Yes — 4`,
    weight: 0.1,
  },
  {
    name: 'geographic_risk',
    label: 'Geographic Risk',
    description: `Where is the vendor headquartered, and where will data be stored or processed? Consider whether the vendor operates in jurisdictions with adequate data protection laws (e.g. EU adequacy decisions), or in countries flagged as high-risk by regulators or internal policy. Assess whether data will be transferred across borders, whether the vendor uses sub-processors in multiple regions, and whether there are geopolitical or sanctions-related risks associated with the vendor's country of operation.

**Factors**

Hosting / processing location.
  - EU / UK / adequacy — 1
  - Other with safeguards (SCCs) — 2
  - High-risk jurisdiction or no safeguards — 5

Government access / disclosure exposure — capped at 4.
  - Low — 1
  - Elevated — 4`,
    weight: 0.05,
  },
]
